import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const statusConfig = {
  requested: { label: 'Requested', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  approved: { label: 'Approved', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  paid: { label: 'Paid', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  active: { label: 'Active', color: '#ffd700', bg: 'rgba(255,215,0,0.12)' },
  completed: { label: 'Completed', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  cancelled: { label: 'Cancelled', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const RentalBookingOversightScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [depositAction, setDepositAction] = useState({ show: false, bookingId: null, status: 'released', notes: '' });

  useEffect(() => {
    fetchBookings();
  }, [filterStatus]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('rental_bookings')
        ?.select(`*, vehicles(title, make, model), user_profiles!rental_bookings_customer_id_fkey(full_name, email, phone)`)
        ?.order('created_at', { ascending: false });
      if (filterStatus !== 'all') query = query?.eq('booking_status', filterStatus);
      const { data, error } = await query;
      if (!error) setBookings(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    setProcessing(bookingId);
    try {
      const { data, error } = await supabase?.rpc('admin_update_booking_status', {
        p_booking_id: bookingId,
        p_admin_id: user?.id,
        p_new_status: newStatus,
      });
      if (error) throw error;
      if (data?.success) {
        setSuccessMsg(`Booking ${newStatus}`);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        fetchBookings();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const handleDepositRelease = async () => {
    setProcessing(depositAction?.bookingId);
    try {
      const { data, error } = await supabase?.rpc('release_rental_deposit', {
        p_booking_id: depositAction?.bookingId,
        p_admin_id: user?.id,
        p_deposit_status: depositAction?.status,
        p_damage_notes: depositAction?.notes || null,
      });
      if (error) throw error;
      if (data?.success) {
        setSuccessMsg('Deposit status updated!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        setDepositAction({ show: false, bookingId: null, status: 'released', notes: '' });
        fetchBookings();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {processing && <SpinnerOverlay message="Processing..." />}
      {showSuccess && <SuccessToast message={successMsg} visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {/* Deposit Modal */}
      {depositAction?.show && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ background: '#0d1f3c', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-white font-bold text-lg mb-4">Deposit Action</h3>
            <div className="space-y-3 mb-4">
              {[
                { value: 'released', label: 'Full Release', desc: 'Return full deposit to customer' },
                { value: 'held', label: 'Keep Hold', desc: 'Continue holding deposit' },
                { value: 'partial_hold', label: 'Partial Hold', desc: 'Hold portion for damages' },
              ]?.map(opt => (
                <label key={opt?.value} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ border: `1px solid ${depositAction?.status === opt?.value ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}`, background: depositAction?.status === opt?.value ? 'rgba(255,215,0,0.06)' : 'transparent' }}>
                  <input type="radio" name="depositStatus" value={opt?.value} checked={depositAction?.status === opt?.value} onChange={e => setDepositAction(d => ({ ...d, status: e?.target?.value }))} className="accent-[#ffd700]" />
                  <div>
                    <div className="text-white font-semibold text-sm">{opt?.label}</div>
                    <div className="text-white/40 text-xs">{opt?.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <textarea rows={3} placeholder="Damage notes (optional)..." value={depositAction?.notes} onChange={e => setDepositAction(d => ({ ...d, notes: e?.target?.value }))}
              className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none resize-none mb-4"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }} />
            <div className="flex gap-3">
              <button onClick={() => setDepositAction({ show: false, bookingId: null, status: 'released', notes: '' })} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleDepositRelease} className="btn-primary flex-1">Confirm</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin-dashboard')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-lg">Rental Booking Oversight</h1>
            <p className="text-white/40 text-xs">Manage all rental bookings, approvals, and deposits</p>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
          {Object.entries(statusConfig)?.map(([key, cfg]) => (
            <button key={key} onClick={() => setFilterStatus(key === filterStatus ? 'all' : key)}
              className="rounded-xl p-3 text-center transition-all hover:scale-[1.02]"
              style={{ background: filterStatus === key ? cfg?.bg : 'rgba(255,255,255,0.04)', border: `1px solid ${filterStatus === key ? cfg?.color + '40' : 'rgba(255,255,255,0.08)'}` }}>
              <div className="font-black text-xl" style={{ color: cfg?.color }}>{bookings?.filter(b => b?.booking_status === key)?.length}</div>
              <div className="text-white/40 text-xs">{cfg?.label}</div>
            </button>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button onClick={() => setFilterStatus('all')} className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all" style={{ background: filterStatus === 'all' ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)', color: filterStatus === 'all' ? '#ffd700' : 'rgba(255,255,255,0.5)', border: `1px solid ${filterStatus === 'all' ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>All</button>
          {Object.entries(statusConfig)?.map(([key, cfg]) => (
            <button key={key} onClick={() => setFilterStatus(key)} className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all capitalize" style={{ background: filterStatus === key ? cfg?.bg : 'rgba(255,255,255,0.06)', color: filterStatus === key ? cfg?.color : 'rgba(255,255,255,0.5)', border: `1px solid ${filterStatus === key ? cfg?.color + '40' : 'rgba(255,255,255,0.1)'}` }}>{cfg?.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3]?.map(i => <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : bookings?.filter(b => filterStatus === 'all' || b?.booking_status === filterStatus)?.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-white/40 text-sm">No bookings found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings?.filter(b => filterStatus === 'all' || b?.booking_status === filterStatus)?.map(booking => {
              const status = statusConfig?.[booking?.booking_status] || statusConfig?.requested;
              const breakdown = booking?.price_breakdown || {};
              const customer = booking?.user_profiles;
              return (
                <div key={booking?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-white font-bold text-base">#{booking?.booking_number}</h3>
                      <p className="text-white/40 text-xs mt-0.5">{booking?.vehicles?.title}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: status?.bg, color: status?.color }}>{status?.label}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
                    <div><div className="text-white/40 mb-0.5">Customer</div><div className="text-white font-semibold">{customer?.full_name || 'N/A'}</div></div>
                    <div><div className="text-white/40 mb-0.5">Dates</div><div className="text-white font-semibold">{formatDate(booking?.start_at)} → {formatDate(booking?.end_at)}</div></div>
                    <div><div className="text-white/40 mb-0.5">Total</div><div className="text-[#ffd700] font-bold">KES {breakdown?.total?.toLocaleString() || '—'}</div></div>
                    <div><div className="text-white/40 mb-0.5">Deposit</div><div className="text-white font-semibold capitalize">{booking?.deposit_status?.replace('_', ' ') || 'held'}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {booking?.booking_status === 'requested' && (
                      <>
                        <button onClick={() => handleStatusUpdate(booking?.id, 'approved')} disabled={!!processing} className="btn-primary text-xs py-2 px-3">
                          <Icon name="Check" size={14} /> Approve
                        </button>
                        <button onClick={() => handleStatusUpdate(booking?.id, 'cancelled')} disabled={!!processing} className="btn-danger text-xs py-2 px-3">
                          <Icon name="X" size={14} /> Cancel
                        </button>
                      </>
                    )}
                    {['approved', 'paid', 'active', 'completed']?.includes(booking?.booking_status) && (
                      <button onClick={() => setDepositAction({ show: true, bookingId: booking?.id, status: 'released', notes: '' })} className="btn-secondary text-xs py-2 px-3">
                        <Icon name="DollarSign" size={14} /> Deposit Action
                      </button>
                    )}
                    {['approved', 'paid', 'active']?.includes(booking?.booking_status) && (
                      <button onClick={() => handleStatusUpdate(booking?.id, 'cancelled')} disabled={!!processing} className="btn-danger text-xs py-2 px-3">
                        Force Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalBookingOversightScreen;
